import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { TEXT_CAPTION } from '../recipes'
import { GRADE_LOOK, reasonsText, type VoiceSampleQuality } from './voice-sample-quality'

export interface VoiceSampleVerdictProps {
  quality: VoiceSampleQuality
}

const COPY = 'humint.voice.sample.verdict'

/** The grades are provisional: said once, small, under the verdict. */
export const PROVISIONAL_NOTE = 'text-[11px] leading-4 text-muted-foreground'

/**
 * The server's verdict on a stored voice sample (ADR-0062), under the ring: *Excellent sample*,
 * *Usable sample* with its reasons, or *Weak sample – record again* with its reasons — an icon
 * and words, never colour alone — then what it means for the subject's reference, and once, small,
 * that the grades are provisional. A weak sample is said as an alert; the others are told through
 * the instrument's status line.
 */
export function VoiceSampleVerdict({ quality }: VoiceSampleVerdictProps) {
  const { t } = useTranslation()
  const { grade } = quality
  const { Icon, tone } = GRADE_LOOK[grade]
  const reasons = reasonsText(quality, t)
  return (
    <div
      role={grade === 'low' ? 'alert' : undefined}
      data-grade={grade}
      className="flex w-full flex-col items-center gap-1.5 text-center"
    >
      <p className="flex items-center gap-2 text-base font-semibold">
        <Icon aria-hidden className={cn('size-5 shrink-0', tone)} />
        {t(`${COPY}.${grade}.title`)}
      </p>
      {reasons && (
        <p
          className={cn(
            TEXT_CAPTION,
            'max-w-[38ch] font-medium text-balance first-letter:uppercase'
          )}
        >
          {reasons}
        </p>
      )}
      <p className={cn(TEXT_CAPTION, 'max-w-[38ch] text-balance text-muted-foreground')}>
        {t(`${COPY}.${grade}.body`)}
      </p>
      <p className={cn(PROVISIONAL_NOTE, 'max-w-[42ch] text-balance')}>
        {t(`${COPY}.provisional`)}
      </p>
    </div>
  )
}
